import { Box, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"

import { ASSOCIATION_TYPE_LABELS, EQUIVALENCY_TYPE_LABELS, FilterField } from "../constants"

import { FilterChip } from "@/components/common/Filters"
import { PRODUCT_STATUS_LABEL_MAP } from "@/constants/product"
import type { AssosiatedProductType, ComputedProductStatus, EquivalencyType } from "@/graphql/codegen/graphql"

interface FilterValues {
  [FilterField.EquivalentTypes]: EquivalencyType[]
  [FilterField.ProductStatuses]: ComputedProductStatus[]
  [FilterField.AssociationTypes]: AssosiatedProductType[]
}

const EquivalentProductGroupsFilterChips: FC = () => {
  const { equivalentTypes, productStatuses, associationTypes } = useSearch({
    from: "/app/admin/manage-products/_layout/equivalents",
    strict: true,
  })
  const navigate = useNavigate({ from: "/app/admin/manage-products/equivalents" })

  // Show chips if any filters are applied
  const hasEquivalencyTypeFilter = equivalentTypes && equivalentTypes.length > 0
  const hasProductStatusFilter = productStatuses && productStatuses.length > 0
  const hasAssociationTypeFilter = associationTypes && associationTypes.length > 0
  const hasFilters = hasEquivalencyTypeFilter || hasProductStatusFilter || hasAssociationTypeFilter

  if (!hasFilters) {
    return null
  }

  const handleClearFilter = <K extends FilterField>(
    filterKey: K,
    valueToRemove: FilterValues[K][number],
    currentValues: FilterValues[K] | undefined
  ) => {
    const updated = currentValues?.filter((item) => item !== valueToRemove)
    navigate({
      search: (prev) => ({
        ...prev,
        [filterKey]: updated?.length ? updated : undefined,
      }),
      replace: true,
    })
  }

  return (
    <Box data-testid='equivalent-groups-filter-chips' display='flex' flexWrap='wrap' alignItems='center' gap={2}>
      <Typography variant='body1' className='text-gray-700'>
        Applied filters:
      </Typography>
      {equivalentTypes?.map((type) => (
        <FilterChip
          key={`type-${type}`}
          label={EQUIVALENCY_TYPE_LABELS[type]}
          onDelete={() => handleClearFilter(FilterField.EquivalentTypes, type, equivalentTypes)}
        />
      ))}
      {productStatuses?.map((status) => (
        <FilterChip
          key={`status-${status}`}
          label={`Status: ${PRODUCT_STATUS_LABEL_MAP[status]}`}
          onDelete={() => handleClearFilter(FilterField.ProductStatuses, status, productStatuses)}
        />
      ))}
      {associationTypes?.map((type) => (
        <FilterChip
          key={`association-${type}`}
          label={`Association: ${ASSOCIATION_TYPE_LABELS[type]}`}
          onDelete={() => handleClearFilter(FilterField.AssociationTypes, type, associationTypes)}
        />
      ))}
    </Box>
  )
}

export default EquivalentProductGroupsFilterChips
